/* eslint-disable import/no-duplicates */
import React, { useMemo } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Center,
  Tooltip,
} from '@chakra-ui/react';
import { RiGitMergeLine, RiFileTextLine } from 'react-icons/ri';
import { formatReportDate } from '../../../../utils/formatReportDate';

import { ModalComponent } from '..';
import { ModalViewExpense } from '../ModalViewExpense';
import { IExpenseReport } from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { useUsers } from '../../../../hooks/useUsers';

interface IModalProps {
  // O relatório consolidado (pai) cujos filhos queremos exibir.
  consolidated: IExpenseReport;
  // Lista completa de relatórios já carregada na tela — usada para
  // localizar os filhos pelo vínculo consolidated_report_id.
  allReports: IExpenseReport[];
}

// O valor de cada item já vem correto na origem (ModalAddNewExpense /
// ModalConsolidateExpenses aplicam a regra de faixa por KM antes de gravar
// `amount`), então aqui é só somar.
const calcTotal = (report: IExpenseReport): number =>
  report.items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

export function ModalConsolidatedChildren({
  consolidated,
  allReports,
}: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { getUserName } = useUsers();

  // Filhos = relatórios cujo consolidated_report_id aponta para este consolidado.
  const children = useMemo(() => {
    return allReports.filter(r => {
      const parentId = r.consolidatedReportId || r.consolidated_report_id;
      return parentId === consolidated.id;
    });
  }, [allReports, consolidated.id]);

  const childrenTotal = useMemo(
    () => children.reduce((sum, r) => sum + calcTotal(r), 0),
    [children],
  );

  return (
    <>
      <Tooltip hasArrow label="Ver relatórios consolidados" placement="top">
        <Button size="sm" colorScheme="green" variant="ghost" onClick={onOpen}>
          <Icon as={RiGitMergeLine} fontSize="20" />
        </Button>
      </Tooltip>

      <ModalComponent
        title="Relatórios consolidados"
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="3xl"
        scrollBehavior="inside"
      >
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Cabeçalho com o resumo do consolidado pai */}
            <Box
              bg="whiteAlpha.100"
              p={4}
              borderRadius="md"
              borderLeft="3px solid"
              borderLeftColor="green.400"
            >
              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.400">
                    Consolidado de
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color="gray.100">
                    {consolidated.director_name ||
                      getUserName(consolidated.director)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatReportDate(
                      consolidated.date,
                      "dd 'de' MMMM 'de' yyyy",
                    )}
                  </Text>
                </VStack>
                <VStack align="end" spacing={0}>
                  <Text fontSize="xs" color="gray.400">
                    Total consolidado
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.300">
                    {formatCurrency(childrenTotal)}
                  </Text>
                  <Badge colorScheme="green" fontSize="2xs">
                    {children.length}{' '}
                    {children.length === 1 ? 'relatório' : 'relatórios'}
                  </Badge>
                </VStack>
              </HStack>
            </Box>

            {children.length === 0 ? (
              <Center py={10}>
                <Flex flexDirection="column" align="center">
                  <Icon
                    as={RiFileTextLine}
                    fontSize="40"
                    color="gray.500"
                    mb={2}
                  />
                  <Text color="gray.400">
                    Nenhum relatório filho encontrado
                  </Text>
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Ative o filtro &quot;Consolidados&quot; para carregar todos
                  </Text>
                </Flex>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" colorScheme="whiteAlpha">
                  <Thead>
                    <Tr>
                      <Th>Data</Th>
                      <Th>Usuário</Th>
                      <Th>Itens</Th>
                      <Th isNumeric>Valor</Th>
                      <Th width="8">Detalhes</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {children.map(child => (
                      <Tr key={child.id} _hover={{ bg: 'whiteAlpha.50' }}>
                        <Td>{formatReportDate(child.date)}</Td>
                        <Td>
                          {child.director_name || getUserName(child.director)}
                        </Td>
                        <Td>
                          <Badge colorScheme="gray" fontSize="2xs">
                            {child.items.length}{' '}
                            {child.items.length === 1 ? 'item' : 'itens'}
                          </Badge>
                        </Td>
                        <Td isNumeric fontWeight="bold">
                          {formatCurrency(calcTotal(child))}
                        </Td>
                        <Td>
                          {/* Drill-through: abre o relatório filho completo */}
                          <ModalViewExpense expense={child} />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="gray" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalComponent>
    </>
  );
}
